/**
 * Orquestrador do Control_Panel (AD-8/R19).
 *
 * Coordena o fluxo autoexplicativo para usuários não técnicos:
 *   configurar → enviar (validação local) → acompanhar (polling) →
 *   baixar (DONE) | explicação de falha (FAILED).
 *
 * Toda a comunicação é feita exclusivamente com a Ingestion_API pública
 * (R19.3) via `MakePubApiClient`. A aplicação é resiliente a falhas de rede/API
 * (R19.8): erros viram estados tratáveis com ação de retomada, sem travar.
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { Header } from "./components/Header";
import { SettingsDialog } from "./components/SettingsDialog";
import { DoneScreen } from "./components/screens/DoneScreen";
import { FailureScreen } from "./components/screens/FailureScreen";
import { ProgressScreen } from "./components/screens/ProgressScreen";
import { UploadScreen } from "./components/screens/UploadScreen";
import type { SourceFormat } from "./config/tiers";
import { useJobPolling } from "./hooks/useJobPolling";
import { ApiError, MakePubApiClient, NetworkError } from "./lib/apiClient";
import {
  DEFAULT_SETTINGS,
  isConfigured,
  loadSettings,
  saveSettings,
  type Settings,
} from "./lib/settings";

type View = "upload" | "progress" | "done" | "failed";

/** Mensagem amigável a partir de um erro de criação/upload (sem detalhes crus). */
function friendlySubmitError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "AuthenticationError":
        return "Sua chave de acesso não foi aceita. Revise-a nas configurações.";
      case "UnsupportedFormat":
        return "Esse formato não é aceito. Envie PDF, Word, Markdown, texto ou HTML.";
      case "FileTooLarge":
        return "O arquivo excede o limite do seu plano. Reduza o tamanho e tente novamente.";
      case "QuotaExceeded":
        return "Você atingiu o limite de conversões do seu plano neste mês.";
      default:
        return err.message || "Não foi possível iniciar o envio. Tente novamente.";
    }
  }
  if (err instanceof NetworkError) {
    return "Não foi possível conectar ao serviço. Verifique sua conexão e tente novamente.";
  }
  return "Ocorreu um erro inesperado. Tente novamente.";
}

export default function App() {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [loaded, setLoaded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  const [view, setView] = useState<View>("upload");
  const [jobId, setJobId] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Carrega configurações locais na montagem.
  useEffect(() => {
    const loadedSettings = loadSettings();
    setSettings(loadedSettings);
    setLoaded(true);
    if (!isConfigured(loadedSettings)) {
      setShowSettings(true);
    }
  }, []);

  const client = useMemo(() => {
    if (!isConfigured(settings)) {
      return null;
    }
    return new MakePubApiClient({ baseUrl: settings.apiBaseUrl, apiKey: settings.apiKey });
  }, [settings]);

  // Polling ativo apenas na tela de acompanhamento.
  const polling = useJobPolling(client, view === "progress" ? jobId : null);

  // Transição para telas terminais quando o polling detecta DONE/FAILED.
  useEffect(() => {
    if (polling.phase === "terminal" && polling.job) {
      if (polling.job.status === "DONE") {
        setView("done");
      } else if (polling.job.status === "FAILED") {
        setView("failed");
      }
    }
  }, [polling.phase, polling.job]);

  const handleSaveSettings = useCallback((next: Settings) => {
    setSettings(saveSettings(next));
    setShowSettings(false);
  }, []);

  const startOver = useCallback(() => {
    setJobId(null);
    setSubmitError(null);
    setDownloadError(null);
    setView("upload");
  }, []);

  const handleSubmit = useCallback(
    async (file: File, format: SourceFormat) => {
      if (!client) {
        setShowSettings(true);
        return;
      }
      setSubmitting(true);
      setSubmitError(null);
      try {
        const created = await client.createJob(format);
        await client.uploadFile(created.upload_url, file, file.type || undefined);
        setJobId(created.job_id);
        setView("progress");
      } catch (err) {
        setSubmitError(friendlySubmitError(err));
      } finally {
        setSubmitting(false);
      }
    },
    [client],
  );

  const handleDownload = useCallback(async () => {
    if (!client || !jobId) {
      return;
    }
    setDownloading(true);
    setDownloadError(null);
    try {
      const { download_url } = await client.getDownloadUrl(jobId);
      // Abre a URL pré-assinada do S3 (dispara o download do EPUB).
      window.open(download_url, "_blank", "noopener,noreferrer");
    } catch (err) {
      if (err instanceof ApiError && err.code === "JobNotReady") {
        setDownloadError("O arquivo ainda está sendo finalizado. Tente novamente em instantes.");
      } else {
        setDownloadError(
          "Não conseguimos preparar o download agora. Tente novamente em instantes.",
        );
      }
    } finally {
      setDownloading(false);
    }
  }, [client, jobId]);

  return (
    <div className="mp-shell">
      <a href="#conteudo" className="skip-link">
        Pular para o conteúdo
      </a>
      <Header onOpenSettings={() => setShowSettings(true)} />

      <main className="mp-main" id="conteudo">
        {view === "upload" ? (
          <UploadScreen
            settings={settings}
            submitting={submitting}
            submitError={submitError}
            onSubmit={handleSubmit}
          />
        ) : null}

        {view === "progress" ? (
          <ProgressScreen
            job={polling.job}
            phase={polling.phase}
            error={polling.error}
            onRetry={polling.retry}
            onStartOver={startOver}
          />
        ) : null}

        {view === "done" ? (
          <DoneScreen
            downloading={downloading}
            downloadError={downloadError}
            onDownload={handleDownload}
            onStartOver={startOver}
          />
        ) : null}

        {view === "failed" ? (
          <FailureScreen
            lastError={polling.job?.last_error ?? null}
            onRetry={startOver}
            onStartOver={startOver}
          />
        ) : null}
      </main>

      <footer className="mp-footer">
        MakePub — conversão profissional de documentos em EPUB acessível.
      </footer>

      {loaded && showSettings ? (
        <SettingsDialog
          initial={settings}
          onSave={handleSaveSettings}
          onClose={() => setShowSettings(false)}
          required={!isConfigured(settings)}
        />
      ) : null}
    </div>
  );
}
